<template>
  <div class="pg-table" :id="idPrefix">
    <PgButtonGroup
      class="pg-table__button"
      :list="tableTopButtons"
      size="mini"
      :compileData="extendCompileData"
      :customAction="customAction"
    >
      <template #left>
        <slot name="left"></slot>
      </template>
      <template #right>
        <div class="pg-table__button-right" v-if="$slots.right || seachKeys.length > 0">
          <slot name="right"></slot>
          <PgInput
            v-if="seachKeys.length > 0"
            class="pg-table__button-right-seach"
            clearable
            :placeholder="seachPlaceholder"
            v-model="seachValue"
            @enter="seachMethodFn"
          >
            <template #prefix><i class="ivu-icon-icon-pg-serach"></i></template>
          </PgInput>
        </div>
      </template>
    </PgButtonGroup>
    <slot name="tableTop"></slot>
    <div
      class="pg-table__table"
      :class="{
        'pg-table__table--stripe': !treeNode && stripe,
        'pg-table__table--mot-stripe': !!treeNode || !stripe,
      }"
    >
      <vxe-table
        :border="border"
        :stripe="!treeNode && stripe"
        ref="tableRef"
        v-loading="loading"
        element-loading-background="rgba(255, 255, 255, 0.4)"
        :auto-resize="autoResize"
        :height="height"
        :maxHeight="maxHeight"
        :show-overflow="showOverflow"
        show-header-overflow
        :show-footer="showFooter"
        :footerMethod="customFooterMethod || footerMethod"
        :tree-config="
          treeNode && {
            expandAll: true,
            ...treeConfig,
          }
        "
        :row-config="{ keyField: '_ID', isHover: true, height: 28, isCurrent: true, ...rowConfig }"
        :sort-config="{ multiple: true, chronological: true, remote: true, ...sortConfig }"
        @sort-change="sortChange"
        :scroll-x="{ gt: 35, oSize: 10, enabled: true }"
        :scroll-y="scrollY"
        @scroll="(param) => scroll?.(param)"
        :column-config="{ resizable: true }"
        :edit-rules="validRules"
        :checkbox-config="{
          showHeader: true,
          highlight: true,
          ...tableCheckboxConfig,
        }"
        :seq-config="{ seqMethod }"
        @current-change="rowChange"
        @cell-click="cellClick"
        @cell-dblclick="cellDblclick"
        :row-class-name="rowClassName"
        :mergeFooterItems="mergeFooterItems"
        :edit-config="{ beforeEditMethod, showIcon: false, autoClear: true, ...editConfig }"
        @edit-closed="editClosed"
      >
        <vxe-column
          v-if="isSelection || isIconSelection"
          type="checkbox"
          :fixed="selectAndSeqFixed"
          width="50"
          align="center"
          footer-class-name="pg-table__table-cell-footer-title"
        >
          <template #header="{ checked, disabled }">
            <Checkbox
              v-show="!hideSelectAll"
              :class="[{ checkbox_opacity: isIconSelection }]"
              :value="checked"
              :disabled="disabled"
              :indeterminate="isIndeterminate(checked)"
              @change="() => checkboxAll(!checked)"
            ></Checkbox>
            <iconSelected
              v-show="!hideSelectAll"
              v-if="isIconSelection"
              :idPrefix="`${idPrefix}_icon`"
              :value="checked"
            ></iconSelected>
          </template>
          <template #checkbox="{ checked, disabled, row }">
            <Checkbox
              :class="[{ checkbox_opacity: isIconSelection }]"
              :value="checked"
              :disabled="disabled"
              @change="() => checkboxChange(row, !checked)"
            ></Checkbox>
            <iconSelected
              v-if="isIconSelection"
              :idPrefix="`${idPrefix}_icon`"
              :value="checked"
              :disabled="disabled"
            ></iconSelected>
          </template>
        </vxe-column>
        <vxe-column
          v-if="seqShow"
          type="seq"
          :fixed="selectAndSeqFixed"
          :width="seqWidth ? seqWidth : treeNode === 'seq' ? 90 : 50"
          title="序号"
          :tree-node="treeNode === 'seq'"
          align="center"
          footer-class-name="pg-table__table-cell-footer-title"
        >
          <template #header>
            <PgColumnCustom :tableConfig="tableConfig" @on-ok="columnCustom"></PgColumnCustom>
          </template>
        </vxe-column>
        <template v-for="(item, index) in columnList">
          <vxe-column
            v-if="!onlyShowFields?.length || onlyShowFields?.includes(item.property)"
            show-footer-overflow
            :tree-node="treeNode === item.property"
            :field="item.property"
            :formatter="formatter"
            :title="item.label"
            :width="item.width"
            :fixed="item.fixed"
            :align="item.align"
            :sortable="item.sort"
            header-align="center"
            :edit-render="editConfig?.enabled && !!item.parameter?.editRowParam ? {} : undefined"
            :visible="columnShow(item, extendCompileData)"
            :params="item"
            :key="index + item.code + index"
          >
            <template #header="{ column }">
              <span :class="{ 'pg-table__table-cell-title--required': column.params.required }">
                {{ column.title }}
                <PgTextTip class="pg-table__table-cell-title-tip" :text="item.title" />
              </span>
              <PgBatchCell
                v-if="column.params?.batchModify"
                :batchRow="batchRow"
                :column="column.params"
                :tableData="tableData"
                :extendCompileData="extendCompileData"
              ></PgBatchCell>
            </template>
            <template
              v-if="item.componentCode || treeNode === item.property"
              #default="{ row, column }"
            >
              <i
                v-if="treeNode === item.property && isTopFloor(row)"
                class="pg-table__table--row-icon ivu-icon-icon-pg-yjfl"
              ></i>
              <i
                v-if="treeNode === item.property && !isTopFloor(row)"
                class="pg-table__table--row-icon-clild ivu-icon-icon-pg-ejfl"
              ></i>
              <cellRender
                :class="{
                  'pg-table__table-read-text-edit-style': editFileds?.[
                    getValueByPath(row, rowConfig?.keyField ?? '_ID')
                  ]?.includes(column.field),
                }"
                :key="item.code"
                :column="item"
                :row="row"
                :masterNotShowFields="masterNotShowFields"
                :treeNode="treeNode"
                :extendCompileData="extendCompileData"
                @change="(cellVal) => cellCange({ row, column, cellVal })"
              ></cellRender>
            </template>
            <template
              #edit="{ row, column }"
              v-if="editConfig?.enabled && !!item.parameter?.editRowParam"
            >
              <!-- <PgInput></PgInput> -->
              <cellRender
                :key="`edit${item.code}`"
                :column="item"
                :row="row"
                :masterNotShowFields="masterNotShowFields"
                :treeNode="treeNode"
                :extendCompileData="extendCompileData"
                editCell
                @change="(cellVal) => editCellCange({ row, column, cellVal })"
              ></cellRender>
            </template>
          </vxe-column>
        </template>
        <slot>
          <vxe-column
            :width="buttonColumnWidth"
            title="操作"
            fixed="right"
            align="center"
            v-if="tableRowButtons.length > 0"
          >
            <template #default="{ row }">
              <PgButtonGroupTable
                :key="row[rowConfig?.keyField ?? '_ID'] + tableData.length"
                :shortcutNum="rowShortcutNum"
                :list="tableRowButtons"
                :compileData="{
                  row,
                  tableIsEditByRow,
                  tableSetEditRow,
                  tableClearEdit,
                  tableCancelRow,
                  ...extendCompileData,
                }"
                :customAction="customAction"
                :onlyIcon="rowButtonOnlyIcon"
              ></PgButtonGroupTable>
            </template>
          </vxe-column>
        </slot>
        <template #empty>
          <slot name="empty">
            <div class="pg-table__table-empty">
              <p></p>
              <p></p>
              <p></p>
            </div>
          </slot>
        </template>
      </vxe-table>
    </div>
    <div class="pg-table__page">
      <div class="pg-table__page-left">
        <slot name="pageLeft"></slot>
      </div>
      <PgPagination
        v-if="showPagination"
        class="pg-table__page-right"
        :total="totalCount"
        :pageSize.sync="page.pageSize"
        :pageSizes="pageSizes"
        :current-page.sync="page.pageNo"
        @size-change="sizeChange"
        @current-change="currentChange"
      />
    </div>
  </div>
</template>
<script setup>
import { Checkbox } from 'element-ui'
import { PgPagination } from '../../pagination/index'
import cellRender from './cell-render'
import iconSelected from './icon-selected.vue'
import { PgButtonGroup, PgButtonGroupTable } from '../../button-group/index'
import { BUTTONGROUP } from '../../../tokens/index'
import { ref, shallowRef, watch, onMounted, inject, computed } from 'vue'
import { useColumn } from '../hook/use-column'
import { useFooter } from '../hook/use-footer'
import { useData } from '../hook/use-data'
import { sortBy } from 'lodash-es'
import { useCheckbox } from '../hook/use-checkbox'
import { useSeach } from '../hook/use-seach'
import PgBatchCell from './batch-cell.vue'
import PgColumnCustom from './column-custom.vue'
import { PgTextTip } from '../../text-tip/index'
import { pgMessage } from '../../message/index'
import { getValueByPath } from '../../../utils/index'

const props = defineProps({
  height: {
    type: [Number, String],
  },
  maxHeight: [Number, String],
  //是否显示斑马条纹
  stripe: {
    type: Boolean,
    default: true,
  },
  border: {
    type: Boolean,
    default: true,
  },
  column: {
    type: [Array, Object],
    default: null,
  },
  columnUrl: {
    type: String,
    default: 'rest/ezcomp/dict/table/fetch/',
  },
  columnTableCode: {
    type: String,
    default: null,
  },
  data: {
    type: Array,
    default: null,
  },
  showOverflow: {
    type: [Boolean, String],
    default: true,
  },
  seqWidth: Number,
  seqHook: Function,
  treeNode: String,
  treeConfig: Object,
  rowConfig: {
    type: Object,
    default: null,
  },
  sortConfig: Object,
  //与vxeTable相同，扩展一个enable属性，支持Boolean与string，是string时会通过模板解析；
  //enable与checkMethod同时存在时enable无效
  checkboxConfig: {
    type: Object,
    default: () => ({ checkField: 'checked' }),
  },
  checkboxBefore: Function,
  // 是否开启复选
  isSelection: { type: Boolean, default: false },
  // 是否开启加号选择
  isIconSelection: { type: Boolean, default: false },
  // 是否隐藏全选
  hideSelectAll: Boolean,
  //是否显示分页
  showPagination: {
    type: Boolean,
    default: false,
  },
  //分页参数-每页显示多少条
  pageSize: {
    type: Number,
    default: 10,
  },
  //分页参数-可切换每页显示多少条的选项
  pageSizes: Array,
  //通过接口获取表格数据配置，也可使用暴露出的loadDataAsync方法实现
  dataUrl: String,
  dataParameters: Object,
  idPrefix: String,
  extendCompileData: Object,
  buttonTop: [String, Array],
  buttonRow: [String, Array],
  rowShortcutNum: Number, //行级按钮快捷显示个数
  customAction: Function,
  buttonColumnWidth: {
    type: [String, Number],
    default: '50',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  seqIndex: {
    type: Number,
    default: 0,
  },
  seqShow: {
    type: Boolean,
    default: true,
  },
  //加载表格数据时是否需要重置滚动条到表格顶部，loadData方法默认不重置，loadDataAsync方法默认要重置
  scrollBar: {
    type: Boolean,
    default: true,
  },
  scroll: Function,
  seachKeys: {
    type: Array,
    default: () => [],
  },
  seachMethod: Function,
  seachPlaceholder: String,
  errorRow: String,
  currentRow: String,
  batchRow: Function, //列批量修改的钩子
  scrollYConfig: Object,
  //是否计算当前页合计
  isPageTotal: {
    type: Boolean,
    default: true,
  },
  //仅展示字段
  onlyShowFields: Array,
  customFooterMethod: Function,
  mergeFooterItems: Array,
  // 主行不显示的字段
  masterNotShowFields: Array,
  editConfig: {
    type: Object,
    default: () => ({ mode: 'row', trigger: 'click', enabled: true }),
  },
  rowButtonOnlyIcon: Boolean,
  //控制复选和序号列是否冻结
  selectAndSeqFixed: {
    type: String,
    default: 'left',
  },
  autoResize: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'size-change',
  'current-change',
  'row-change',
  'cell-click',
  'cell-click-all',
  'cell-dblclick',
  'checkbox-change',
  'checkbox-all',
  'loaded-async',
  'sort-change',
  'column-custom',
])

// 纵向虚拟滚动配置
const scrollY = computed(() => {
  if (!props.height && !props.maxHeight) {
    return undefined
  } else {
    return { gt: 50, enabled: true, ...props.scrollYConfig }
  }
})

const { getButtonByKey } = inject(BUTTONGROUP, {
  getButtonByKey: (val) => (Array.isArray(val) ? val : []),
})
const tableTopButtons = getButtonByKey(props.buttonTop)
const tableRowButtons = getButtonByKey(props.buttonRow)

//表格ref
const tableRef = ref(null)
//表格所有数据
const tableData = shallowRef([])

const { tableConfig, columnList, columnCustom, validRules, columnShow, formatter } = useColumn(
  props,
  tableRef,
  props.extendCompileData,
  emit
)

const {
  page,
  totalCount,
  sizeChange,
  currentChange,
  loadPageData,
  total,
  loadPageDataAsync,
  creatRowId,
  clearOb,
  getData,
  seachData,
  rowClassName,
  isTopFloor,
} = useData(props, tableRef, tableData, columnList, tableConfig, emit)

const seqMethod = ({ rowIndex, seq, row }) => {
  if (props.seqHook) {
    return props.seqHook({ rowIndex, seq, row })
  }
  if (rowIndex !== -1) {
    return rowIndex + 1 + props.seqIndex + (page.pageNo - 1) * page.pageSize
  } else {
    return seq.replace('.', '-')
  }
}

const { footerMethod, showFooter } = useFooter(props, tableData, tableConfig, total)

const {
  setIndeterminate, // 设置表头复选框是否半选中
  isIndeterminate, // 表头复选框半选中
  checkboxChange, // 表格行级复选框事件
  checkboxAll, // 表头复选框事件
  setCheckboxRow, //设置选中行
  setAllCheckboxRow, //设置表格全选中
  tableCheckboxConfig,
} = useCheckbox(
  {
    tableRef,
    checkboxConfig: props.checkboxConfig,
    treeNode: props.treeNode,
    checkboxBefore: props.checkboxBefore,
    extendCompileData: props.extendCompileData,
  },
  emit
)

//搜索
const { seachValue, seachMethodFn } = useSeach({
  tableData,
  seachKeys: props.seachKeys,
  seachMethodProp: props.seachMethod,
  dataUrl: props.dataUrl,
  page,
  getData,
  seachData: (data) => {
    setIndeterminate(false)
    return seachData(data)
  },
})

//加载表格数据，当initPageNo为true时将分页设置为第一页
const loadData = (data, initPageNo, clearSelect) => {
  if (clearSelect && tableData.value.length > 0) {
    setAllCheckboxRow(false)
  }
  tableData.value = creatRowId(data) ?? []
  setIndeterminate(false)
  clearEditFields()
  return loadPageData(initPageNo)
}

/***
 * 异步加载表格数据
 * 接受一个异步函数，返回结构为{data:[], totalCount, total}
 */
const loadDataAsync = async (getDataMethod) => {
  const data = await getDataMethod?.(page, seachValue.value)
  tableData.value = creatRowId(data?.data) ?? []
  setIndeterminate(false)
  clearEditFields()
  if (data?.hasOwnProperty('totalCount')) {
    return loadPageDataAsync(data ?? {})
  } else {
    return loadPageData()
  }
}

//返回表格全数据和当前表格渲染的数据
const getTableData = () => {
  const { tableData: pageData } = tableRef.value?.getTableData()
  return {
    fullData: tableData.value,
    pageData,
  }
}

onMounted(() => Array.isArray(props.data) && loadData(props.data))
watch(
  () => props.data?.length,
  () => Array.isArray(props.data) && loadData(props.data)
)

const cellCange = ({ row, column }) => {
  tableRef.value.updateStatus({ row, column })
  tableRef.value.updateFooter()
}

const rowChange = (param) => {
  emit('row-change', param)
}
const cellClick = (param) => {
  if (param.column.title !== '操作' && !!param.column.params) {
    emit('cell-click', param)
  }
  emit('cell-click-all', param)
}

const cellDblclick = (param) => {
  if (param.column.title !== '操作' && !!param.column.params) {
    emit('cell-dblclick', param)
  }
}

const sortChange = (param) => emit('sort-change', param)

let editRow = null
const beforeEditMethod = ({ row }) => {
  editRow = JSON.parse(JSON.stringify(row))
  return true
}

const editFileds = shallowRef(null)
const clearEditFields = (row) => {
  if (row) {
    const key = getValueByPath(row, props.rowConfig?.keyField ?? '_ID')
    if (editFileds.value?.[key]) {
      delete editFileds.value[key]
    }
  } else {
    editFileds.value = null
  }
}
const editCellCange = ({ row, column }) => {
  const keyStr = getValueByPath(row, props.rowConfig?.keyField ?? '_ID')
  const keyVal = editFileds.value?.[keyStr] ?? []
  editFileds.value = { ...editFileds.value, [keyStr]: [...new Set([...keyVal, column.field])] }
  cellCange({ row, column })
}
// 判断是否处于编辑状态
const tableIsEditByRow = ({ row }) => tableRef.value.isEditByRow(row)
// 设置行为编辑状态
const tableSetEditRow = ({ row }) => {
  return tableRef.value.setEditRow(row)
}
// 编辑行保存的后置动作
const tableClearEdit = ({ row, newRow }) => {
  clearEditFields(row)
  if (newRow) {
    Object.assign(row, newRow)
  }
  editRow = null
  return tableRef.value.clearEdit(row)
}
// 行编辑状态关闭后执行的方法
const editClosed = () => {
  if (editRow) {
    pgMessage.success({
      message: '未保存',
    })
  }
}
// 取消行的编辑状态
const tableCancelRow = ({ row }) => {
  if (editRow) Object.assign(row, editRow)
  clearEditFields(row)
  pgMessage.success({
    message: '已取消',
  })
  editRow = null
  tableRef.value.clearEdit(row)
}
//校验表格数据
const fullValid = async () => {
  const errorMap = await tableRef.value.fullValidate(true).catch((err) => err)
  if (errorMap) {
    const rowMap = []
    for (const filed of Object.keys(errorMap)) {
      for (const err of errorMap[filed]) {
        rowMap.push({
          row: err.rowIndex + 1,
          message: `${err.column.title}${err.rule.message}`,
          level: 1,
        })
      }
    }
    return sortBy(rowMap, (o) => o.row)
  }
}

defineExpose({
  loadData,
  loadDataAsync,
  getTableData,
  setCheckboxRow,
  setAllCheckboxRow,
  setCurrentRow: (row) => tableRef.value?.setCurrentRow?.(row),
  clearCurrentRow: () => tableRef.value?.clearCurrentRow(),
  setAllTreeExpand: (checked) => tableRef.value?.setAllTreeExpand(checked),
  setTreeExpand: (rows, checked) => tableRef.value?.setTreeExpand(rows, checked),
  fullValid,
  recalculate(val) {
    //重新计算表格，如果传 true 则进行完整计算（对于某些特殊场景可能会用到，比如隐藏的表格、重新计算列宽...等）
    tableRef.value?.recalculate?.(val)
  },
  clearOb,
  creatRowId,
  getScroll: () => tableRef.value.getScroll(),
  scrollTo: (scrollLeft, scrollTop) => tableRef.value.scrollTo(scrollLeft, scrollTop),
  scrollToRow: (row, fieldOrColumn) => tableRef.value.scrollToRow(row, fieldOrColumn),
})
</script>
<style lang="scss">
@import '../style/table-main.scss';
</style>
